import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
  Box,
  InputAdornment,
  Skeleton,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  Stack,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Search,
  FilterList,
  ArrowUpward,
  ArrowDownward,
} from "@mui/icons-material";
import { CONSTANTS } from "../../../../library/common/constant";
import BookItem from "./BookItem";
import { Pagination } from "../../../../library/component";
import styles from "./BookListing.module.scss";
import { api } from "../../../../services";
import MultiInput from "../../../../library/common/MultiInput";

const {
  ALL_BOOKS_TITLE,
  SEARCH_BOOKS_LABEL,
  CATEGORY,
  LANGUAGE,
  BOOK_TITLE,
  BOOK_SLUG,
  FILE,
  ACTIONS,
} = CONSTANTS;

// Tab values map to API type param
const TAB_TYPES = ["book", "literature"];
const TAB_LABELS = ["Granth (Books)", "Articles"];
const TAB_ADD_PATHS = ["/book/add", "/article/add?type=literature"];
const TAB_ADD_LABELS = ["Add Granth", "Add Article"];

function BookList() {
  const navigate = useNavigate();
  const location = useLocation();
  const isArticlePath = location.pathname.startsWith("/article");
  const initialTab = isArticlePath ? 1 : 0;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [filters, setFilters] = useState({
    pageNo: 0,
    pageSize: 10,
    search: "",
    categories: "",
    language: "",
    sortBy: "",
    order: "asc",
    type: TAB_TYPES[initialTab],
  });
  const [modalFilters, setModalFilters] = useState({});
  const [modalSelectedValues, setModalSelectedValues] = useState({});
  const [books, setBooks] = useState([]);
  const [totalBooks, setTotalBooks] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState({ filters: false, sort: false });
  const [categories, setCategories] = useState([]);
  const [languages, setLanguages] = useState([]);
  const anchorRef = useRef(null);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  // Helper to update filters and reset page number.

  const updateFilters = useCallback((newFilter, resetPage = true) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilter,
      pageNo: resetPage ? 0 : newFilter.pageNo ?? prev.pageNo,
    }));
  }, []);

  // Fetch books API call.
  const fetchData = async () => {
    try {
      setLoading(true);
      const activeFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== "")
      );
      // Adjust pageNo (API expects 1-indexed page numbers)
      const response = await api.get("/books", {
        params: { ...activeFilters, pageNo: filters?.pageNo + 1 },
      });

      setTotalBooks(response?.meta?.total ?? 0);
      setBooks(response?.data?.books || []);
    } catch (error) {
      console.error("Error fetching books:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleTabChange(null, isArticlePath ? 1 : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleTabChange = (_, newTab) => {
    setActiveTab(newTab);
    updateFilters({ type: TAB_TYPES[newTab] });
    // Navigate to appropriate list route
    navigate(newTab === 1 ? "/article/list" : "/book/list", { replace: true });
  };

  // Handlers for main filters.
  const handleFilterChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      updateFilters({ [name]: value });
    },
    [updateFilters]
  );

  // Modal filter change handler.
  const handleModalFilterChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;
    // If the field returns an array (from MultiInput), join the values.
    if (["categories", "languages", "tags"].includes(name)) {
      newValue = Array.isArray(newValue) ? newValue.join(",") : newValue;
    }
    setModalFilters((prev) => ({ ...prev, [name]: newValue }));
  }, []);

  // Pagination handlers.
  const handlePageChange = (event, newPage) => {
    updateFilters({ pageNo: newPage }, false);
  };

  const handleRowsPerPageChange = (event) => {
    updateFilters({ pageSize: parseInt(event.target.value, 10) });
  };

  // Modal and sort handlers.
  const openFilterModal = () => setIsOpen({ filters: true, sort: false });
  const openSortModal = () => setIsOpen({ filters: false, sort: true });
  const closeModal = () => setIsOpen({ filters: false, sort: false });

  // Toggle sorting for a given field.
  const handleSortToggle = (sortField) => {
    setFilters((prev) => {
      if (prev.sortBy !== sortField) {
        return { ...prev, sortBy: sortField, order: "asc", pageNo: 0 };
      } else {
        if (prev.order === "asc") {
          return { ...prev, order: "desc", pageNo: 0 };
        } else if (prev.order === "desc") {
          return { ...prev, sortBy: "", order: "asc", pageNo: 0 };
        }
      }
      return prev;
    });
  };

  // Apply modal filters to main filters.
  const applyModalFilters = () => {
    updateFilters(modalFilters);
    closeModal();
  };

  // Fetch categories.
  const fetchCategories = async () => {
    try {
      const { data } = await api.get("/categories/?isBook=true");
      setCategories(data?.categories || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // Fetch languages.
  const fetchLanguages = async () => {
    try {
      const { data } = await api.get("/languages");
      setLanguages(data?.languages || []);
    } catch (error) {
      console.error("Error fetching languages:", error);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchLanguages();
  }, []);

  return (
    <Paper className={styles.paper_container}>
      <Box className={styles.header_search_add_container}>
        <Typography className={styles.header_title} textAlign="center">
          {ALL_BOOKS_TITLE}
        </Typography>
      </Box>

      {/* Granth / Article Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 1 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="book type tabs">
          {TAB_LABELS.map((label, i) => (
            <Tab key={i} label={label} id={`book-tab-${i}`} aria-controls={`book-tabpanel-${i}`} />
          ))}
        </Tabs>
      </Box>

      <Box
        className={styles.search_add_container}
        sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}
      >
        <TextField
          className={styles.search_field}
          placeholder={SEARCH_BOOKS_LABEL || "Search..."}
          variant="outlined"
          size="small"
          name="search"
          value={filters?.search}
          onChange={handleFilterChange}
          sx={{ flex: 1, minWidth: { xs: "100%", md: 400 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ flexGrow: 1 }} />
        <Button
          color="primary"
          variant="contained"
          size="medium"
          onClick={() => navigate(TAB_ADD_PATHS[activeTab])}
        >
          {TAB_ADD_LABELS[activeTab]}
        </Button>
        <Button ref={anchorRef} variant="outlined" onClick={openSortModal}>
          Sort By
        </Button>
        <Menu
          open={isOpen.sort}
          anchorEl={anchorRef.current}
          onClose={closeModal}
          disableAutoFocusItem
        >
          <MenuItem onClick={() => handleSortToggle("createdAt")}>
            <Box display="flex" alignItems="center" width="100%">
              <span>Created Date</span>
              {filters?.sortBy === "createdAt" && (
                <Box ml="auto">
                  {filters?.order === "asc" ? (
                    <ArrowUpward fontSize="small" />
                  ) : (
                    <ArrowDownward fontSize="small" />
                  )}
                </Box>
              )}
            </Box>
          </MenuItem>
          <MenuItem onClick={() => handleSortToggle("title.en")}>
            <Box display="flex" alignItems="center" width="100%">
              <span>Title</span>
              {filters?.sortBy === "title.en" && (
                <Box ml="auto">
                  {filters?.order === "asc" ? (
                    <ArrowUpward fontSize="small" />
                  ) : (
                    <ArrowDownward fontSize="small" />
                  )}
                </Box>
              )}
            </Box>
          </MenuItem>
        </Menu>
        <IconButton
          onClick={openFilterModal}
          className={styles.filterButton}
          color="primary"
        >
          <FilterList />
        </IconButton>
      </Box>

      {/* Filters Modal */}
      <Dialog open={isOpen.filters} onClose={closeModal}>
        <DialogTitle>Filters</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <MultiInput
              options={categories}
              initialValues={modalSelectedValues?.categories || []}
              onChange={handleModalFilterChange}
              name="categories"
              label={CATEGORY}
              type="object"
              setFinalValues={setModalSelectedValues}
              select
            />
            <MultiInput
              options={languages}
              initialValues={modalSelectedValues?.languages || []}
              onChange={handleModalFilterChange}
              name="languages"
              label={LANGUAGE}
              getLabelText={(i) => `${i.name.en}-${i.name.hi} (${i.code})`}
              type="object"
              setFinalValues={setModalSelectedValues}
              select
            />
            <MultiInput
              onChange={handleModalFilterChange}
              initialValues={modalSelectedValues?.tags || []}
              name="tags"
              label="Tags"
              setFinalValues={setModalSelectedValues}
              select
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="featured"
                  checked={modalFilters?.featured || false}
                  onChange={handleModalFilterChange}
                />
              }
              label="Only Featured Books"
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="upcoming"
                  checked={modalFilters?.upcoming || false}
                  onChange={handleModalFilterChange}
                />
              }
              label="Only Upcoming Books"
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="bookSeries"
                  checked={modalFilters?.bookSeries || false}
                  onChange={handleModalFilterChange}
                />
              }
              label="Only Books from BookSeries"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal}>Cancel</Button>
          <Button
            onClick={applyModalFilters}
            variant="contained"
            color="primary"
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Books Table */}
      <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
        <Table stickyHeader aria-label="book table" sx={{ minWidth: 600 }}>
          <TableHead>
            <TableRow>
              <TableCell className={styles.table_header_cell}>S.No</TableCell>
              <TableCell className={styles.table_header_cell}>
                {BOOK_SLUG || "Slug"}
              </TableCell>
              <TableCell className={styles.table_header_cell}>
                {BOOK_TITLE || "Title"}
              </TableCell>
              <TableCell className={styles.table_header_cell}>
                {LANGUAGE || "Language"}
              </TableCell>
              <TableCell align="center" className={styles.table_header_cell}>
                {FILE || "File"}
              </TableCell>
              <TableCell className={styles.table_header_cell} align="center">
                {ACTIONS || "Actions"}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={`skeleton-${index}`}>
                  {Array.from({ length: 6 }).map((_, idx) => (
                    <TableCell key={idx}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : books?.length > 0 ? (
              books?.map((item, index) => (
                <BookItem
                  key={`book-${index}`}
                  index={filters?.pageSize * filters?.pageNo + index + 1}
                  bookItem={item}
                  getBookList={fetchData}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} className={styles.no_data_cell}>
                  <Typography variant="h6" align="center">
                    No granths available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination
        totalCount={totalBooks}
        pageSize={filters?.pageSize}
        pageNo={filters?.pageNo}
        pageChangeHandler={handlePageChange}
        rowsPerPageHandler={handleRowsPerPageChange}
      />

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((p) => ({ ...p, open: false }))}
          severity={toast.severity}
          variant="filled"
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

export default BookList;
